'use client'

import { deleteDB } from 'idb'
import { useCallback, useEffect } from 'react'
import { toast } from 'sonner'

import { APP_VERSION, INDEX_DB_NAME, VERSION_STORAGE_KEY } from '../utilities/constant'
import { useLocale } from './LocaleContext'

/**
 * App Version Buster — Client-side storage clearing on deployment version change.
 * Bump APP_VERSION const per deployment to trigger full clear after Sonner toast confirm.
 */
export function AppVersionBuster() {
  const { translatedData } = useLocale()

  const clearCache = useCallback(() => {
    try {
      caches.keys().then(function (keys) {
        Promise.all(
          keys.map(function (k) {
            caches.delete(k)
          }),
        )
      })
      console.log(' *** Cleared cache *** ')
    } catch (e) {
      console.warn('*Cache clear failed * : ', e)
    }
  }, [])

  const clearLocalStorage = useCallback(() => {
    try {
      localStorage.clear()
      console.log('  *** Cleared localStorage *** ')
    } catch (e) {
      console.warn('* localStorage clear failed * : ', e)
    }
  }, [])

  const clearIndexedDB = useCallback(async () => {
    try {
      deleteDB(INDEX_DB_NAME)
      console.log(' *** Cleared IndexedDB *** ')
    } catch (e) {
      console.warn(' * IndexedDB clear failed * : ', e)
    }
  }, [])

  const clearCookies = useCallback(() => {
    try {
      document.cookie.split(';').forEach((cookie) => {
        const eqPos = cookie.indexOf('=')
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        }
      })
      console.log('  *** Cleared cookies *** ')
    } catch (e) {
      console.warn(' * Cookies clear failed * : ', e)
    }
  }, [])

  const checkAndClear = useCallback(() => {
    try {
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY)
      if (!storedVersion) {
        clearIndexedDB()
        localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION)
        console.log('No stored version found. Setting current version and skipping clear.')
        return
      }
      if (storedVersion === APP_VERSION) {
        return
      }
      toast.dismiss()
      toast.message(
        translatedData?.toastMessages.new_app_version_available ?? '🆕 New app version available!',
        {
          description:
            translatedData?.toastMessages?.clear_stored_data_description ??
            'Clear stored data and refresh? Your current session data will be lost.',
          duration: Infinity,
          dismissible: true,
          action: {
            label: translatedData?.toastMessages?.clear_and_update ?? 'Clear & Update',
            onClick: () => {
              clearLocalStorage()
              clearIndexedDB()
              clearCookies()
              clearCache()
              localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION)
              console.log('Cleared. Reloading...')
              window.location.reload()
            },
          },
          cancel: {
            label: translatedData?.toastMessages?.later ?? 'Later',
            onClick: () => {
              console.log('User postponed update')
              toast.dismiss()
            },
          },
        },
      )

      console.log('Version mismatch')
    } catch (e) {
      console.error('Version Check failed:', e)
    }
  }, [clearLocalStorage, clearIndexedDB, clearCookies, translatedData, clearCache])

  useEffect(() => {
    checkAndClear()

    const handleFocus = () => checkAndClear()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkAndClear()
    }

    window.addEventListener('focus', handleFocus, { once: true })
    document.addEventListener('visibilitychange', handleVisibility, { once: true })

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [checkAndClear])

  return null
}
